import { useEffect } from "react";
import "@/App.css";
import "@/i18n";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteProvider } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { readPending } from "@/lib/payment";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import CmsPage from "@/pages/CmsPage";
import Betaling from "@/pages/Betaling";
import Beheer from "@/pages/beheer/Beheer";
import StaffLogin from "@/pages/beheer/StaffLogin";
import IntegratieSpec from "@/pages/IntegratieSpec";
import Snelbestellen from "@/pages/Snelbestellen";
import Registreren from "@/pages/Registreren";
import Login from "@/pages/Login";
import WachtwoordVergeten from "@/pages/WachtwoordVergeten";
import WachtwoordReset from "@/pages/WachtwoordReset";
import Wachten from "@/pages/Wachten";
import Catalogus from "@/pages/Catalogus";
import ProductDetail from "@/pages/ProductDetail";
import Mandje from "@/pages/Mandje";
import Bestellen from "@/pages/Bestellen";
import Besteld from "@/pages/Besteld";
import MijnBestellingen from "@/pages/MijnBestellingen";
import Account from "@/pages/Account";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

/** Betaald maar order nog niet geplaatst? Bij het volgende bezoek afronden. */
const PendingPaymentResume = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isAuthenticated || pathname.startsWith("/betaling/")) return;
    const pending = readPending();
    if (pending?.reference) {
      navigate(`/betaling/${encodeURIComponent(pending.reference)}`, { replace: true });
    }
  }, [isAuthenticated, pathname, navigate]);
  return null;
};

const DocumentTitle = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t("meta.title");
    document.documentElement.lang = i18n.language;
  }, [t, i18n.language]);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
        <CartProvider>
          <ScrollToTop />
          <DocumentTitle />
          <PendingPaymentResume />
          <Routes>
            <Route path="/stafflogin" element={<StaffLogin />} />
            <Route path="/beheer" element={<Beheer />} />
            <Route path="/integratie/:token" element={<IntegratieSpec />} />
            <Route element={<Layout />}>
              {/* Public */}
              <Route path="/" element={<CmsPage slug="home" />} />
              <Route path="/assortiment" element={<CmsPage slug="assortiment" />} />
              <Route path="/registreren" element={<Registreren />} />
              <Route path="/login" element={<Login />} />
              <Route path="/wachtwoord-vergeten" element={<WachtwoordVergeten />} />
              <Route path="/wachtwoord-reset" element={<WachtwoordReset />} />
              <Route path="/wachten" element={<Wachten />} />
              <Route path="/producten/:id" element={<ProductDetail />} />

              {/* Gated */}
              <Route element={<ProtectedRoute />}>
                <Route path="/producten" element={<Catalogus />} />
                <Route path="/mandje" element={<Mandje />} />
                <Route path="/bestellen" element={<Bestellen />} />
                <Route path="/besteld/:nummer" element={<Besteld />} />
                <Route path="/betaling/:reference" element={<Betaling />} />
                <Route path="/mijn-bestellingen" element={<MijnBestellingen />} />
                <Route path="/snelbestellen" element={<Snelbestellen />} />
                <Route path="/account" element={<Account />} />
              </Route>

              {/* Zelfgemaakte CMS-pagina's zijn direct bereikbaar op /<slug> */}
              <Route path="/:slug" element={<CmsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </CartProvider>
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
